import React, { type JSX } from "react";

type TypographyVariant = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "lead" | "paragraph" | "small";
type TypographyColor = "blue-gray" | "black" | "white" | "gray" | "blue" | "red" | "green" | "orange" | "inherit";

interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  variant?: TypographyVariant;
  color?: TypographyColor;
  as?: keyof JSX.IntrinsicElements;
}

const variantClasses: Record<TypographyVariant, string> = {
  h1: "text-5xl font-semibold leading-tight",
  h2: "text-4xl font-semibold leading-tight",
  h3: "text-3xl font-semibold leading-snug",
  h4: "text-2xl font-semibold leading-snug",
  h5: "text-xl font-semibold leading-snug",
  h6: "text-lg font-semibold leading-normal",
  lead: "text-xl font-normal leading-relaxed",
  paragraph: "text-base font-normal leading-relaxed",
  small: "text-sm font-normal leading-normal",
};

const colorClasses: Record<TypographyColor, string> = {
  "blue-gray": "text-blue-gray-900",
  black: "text-black",
  white: "text-white",
  gray: "text-gray-700",
  blue: "text-blue-500",
  red: "text-red-500",
  green: "text-green-500",
  orange: "text-orange-500",
  inherit: "text-inherit",
};

export const Typography: React.FC<TypographyProps> = ({
  children,
  variant = "paragraph",
  color = "blue-gray",
  as,
  className = "",
  ...props
}) => {
  const Component = as || (variant.startsWith("h") ? variant : "p") as keyof JSX.IntrinsicElements;
  const variantClass = variantClasses[variant] || variantClasses.paragraph;
  const colorClass = colorClasses[color] || colorClasses["blue-gray"];

  return React.createElement(
    Component,
    {
      className: `${variantClass} ${colorClass} ${className}`,
      ...props,
    },
    children
  );
};